import { getCosCredential } from './cos-repo';
import {
  cosErrorToMessage,
  cosGetObjectBlobByFetch,
  cosPutObject,
  cosPutObjects,
  getObjectUrl,
  normalizeCosKey,
  type CosObjectBlobProgressDetail,
} from './cos-client';
import { attachSignedUrls as attachSignedUrlsImpl, type AttachSignedUrlsOptions } from './cos-signed-url';
import type { ICosService } from '../../interfaces/cos-service.interface';

export class CosService implements ICosService {
  async getCredential() {
    const res = await getCosCredential();
    return res.data;
  }

  normalizeObjectKey(pathOrKey: string): string {
    return normalizeCosKey(pathOrKey);
  }

  formatCosError(error: unknown): string {
    return cosErrorToMessage(error);
  }

  async getSignedUrl(objectKey: string): Promise<string> {
    const key = normalizeCosKey(String(objectKey ?? '').trim());
    if (!key) return '';
    return getObjectUrl(key) as Promise<string>;
  }

  attachSignedUrls<T>(
    items: readonly T[],
    options: AttachSignedUrlsOptions<T>,
  ): Promise<T[]> {
    return attachSignedUrlsImpl(items, options);
  }

  async uploadObject(
    key: string,
    body: Blob | File,
    onProgress?: (p: number) => void,
  ): Promise<{ key: string }> {
    const uploaded = await cosPutObject({ key, body, onProgress });
    return { key: uploaded.key };
  }

  async uploadObjects(
    files: Array<{ key: string; body: Blob | File; contentType?: string }>,
    concurrency?: number,
    onItemProgress?: (index: number, percent0to100: number) => void,
  ): Promise<Array<{ key: string }>> {
    const uploaded = await cosPutObjects({
      items: files,
      concurrency,
      onItemProgress,
    });
    return uploaded.map((item) => ({ key: item.key }));
  }

  async fetchObjectBlob(
    pathOrKey: string,
    opts?: {
      onProgress?: (percent0to100: number) => void;
      onProgressDetail?: (detail: CosObjectBlobProgressDetail) => void;
    },
  ): Promise<Blob> {
    return cosGetObjectBlobByFetch(pathOrKey, opts);
  }
}
